import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { LocalizedStrings, getStrings, getCurrentLanguage } from '../locales/strings';

interface LocalizationContextType {
  language: 'ar' | 'en';
  setLanguage: (lang: 'ar' | 'en') => void;
  strings: LocalizedStrings;
  isRTL: boolean;
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

interface LocalizationProviderProps {
  children: ReactNode;
}

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<'ar' | 'en'>('ar');
  const [strings, setStrings] = useState<LocalizedStrings>(getStrings('ar'));

  // Initialize language from localStorage or default to Arabic
  useEffect(() => {
    const savedLanguage = localStorage.getItem('morafaa-language') as 'ar' | 'en' | null;
    const detectedLanguage = savedLanguage || 'ar'; // Default to Arabic instead of browser detection
    setLanguageState(detectedLanguage);
    setStrings(getStrings(detectedLanguage));
    
    // Ensure document direction is set correctly from the start
    document.documentElement.dir = detectedLanguage === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = detectedLanguage;
  }, []);

  const setLanguage = (lang: 'ar' | 'en') => {
    setLanguageState(lang);
    setStrings(getStrings(lang));
    localStorage.setItem('morafaa-language', lang);
    
    // Update document direction
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  };

  const isRTL = language === 'ar';

  return (
    <LocalizationContext.Provider value={{ language, setLanguage, strings, isRTL }}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = (): LocalizationContextType => {
  const context = useContext(LocalizationContext);
  if (context === undefined) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
};
